import styled, { css } from 'styled-components';

import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import { scale010, spaceXXS, xdlColorFocusRing } from '@xing-com/tokens';

import type { ItemSnapPosition } from './item.types';

type ItemTransientProps = {
  $itemMaxWidth?: string;
  $itemMinWidth?: string;
  $itemSnapPosition?: ItemSnapPosition;
  $itemSpacing?: string;
  $itemSpacingConfined?: string;
  $itemSpacingWide?: string;
  $itemWidth?: string;
  $itemWidthConfined?: string;
  $itemWidthWide?: string;
  $lastItemSpace?: string;
  $lastItemSpaceConfined?: string;
  $lastItemSpaceWide?: string;
};

// *** Item Snap Position ***
export const itemSnapPositions = {
  left: css`
    scroll-snap-align: start;
  `,
  center: css`
    scroll-snap-align: center;
  `,
  right: css`
    scroll-snap-align: end;
  `,
};

/**
 * Fake padding on last item
 *
 * To be able to add a right padding to an overflow
 * container, an after element needs to be added that
 * simulates the padding. */
const fakePadding = css`
  content: '';
  display: flex;
  height: 1px;
  position: absolute;
`;

export const Item = styled.div<ItemTransientProps>`
  flex-shrink: 0;
  position: relative;

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:last-child {
    && {
      margin-inline-end: 0;
    }
  }

  ${({ $itemSpacing }) =>
    $itemSpacing &&
    css`
      margin-inline-end: ${() => $itemSpacing};
    `}

  ${({ $itemSpacingConfined }) =>
    $itemSpacingConfined &&
    css`
      @media ${mediaConfined} {
        margin-inline-end: ${() => $itemSpacingConfined};
      }
    `}

  ${({ $itemSpacingWide }) =>
    $itemSpacingWide &&
    css`
      @media ${mediaWide} {
        margin-inline-end: ${() => $itemSpacingWide};
      }
    `}

  ${({ $itemWidth }) =>
    $itemWidth &&
    css`
      width: ${() => $itemWidth};
    `}

  ${({ $itemWidthConfined }) =>
    $itemWidthConfined &&
    css`
      @media ${mediaConfined} {
        width: ${() => $itemWidthConfined};
      }
    `}

  ${({ $itemWidthWide }) =>
    $itemWidthWide &&
    css`
      @media ${mediaWide} {
        width: ${() => $itemWidthWide};
      }
    `}

  ${({ $itemMaxWidth }) =>
    $itemMaxWidth &&
    css`
      max-width: ${() => $itemMaxWidth};
    `}

  ${({ $itemMinWidth }) =>
    $itemMinWidth &&
    css`
      min-width: ${() => $itemMinWidth};
    `}

  ${({ $itemSnapPosition }) => {
    switch ($itemSnapPosition) {
      case 'left':
        return itemSnapPositions.left;
      case 'center':
        return itemSnapPositions.center;
      case 'right':
        return itemSnapPositions.right;
      default:
        return $itemSnapPosition satisfies undefined;
    }
  }}

  ${({ $lastItemSpace }) =>
    $lastItemSpace &&
    css`
      &:last-child::after {
        ${fakePadding};
        inset-inline-end: -${() => $lastItemSpace};
        width: ${() => $lastItemSpace};
      }
    `}

  ${({ $lastItemSpaceConfined }) =>
    $lastItemSpaceConfined &&
    css`
      @media ${mediaConfined} {
        &:last-child::after {
          ${fakePadding};
          inset-inline-end: -${() => $lastItemSpaceConfined};
          width: ${() => $lastItemSpaceConfined};
        }
      }
    `}

  ${({ $lastItemSpaceWide }) =>
    $lastItemSpaceWide &&
    css`
      @media ${mediaWide} {
        &:last-child::after {
          ${fakePadding};
          inset-inline-end: -${() => $lastItemSpaceWide};
          width: ${() => $lastItemSpaceWide};
        }
      }
    `}
`;
